import { useCallback, useContext, useEffect, useState } from "react";
import ComparativeAnalyticsComponent from "./comparativeAnalytics.component";
import { AuthContext, useAuth } from "@/utilities";
import institutesData from "@/api/JSON/InstitutesFresh.json";
import facultyService from "@/api/faculty.service";
import careersService from "@/api/careers.service";
import { OptionSelect } from "./comparativeAnalytics.model";

const dataJSON = JSON.parse(JSON.stringify(institutesData));

const ComparativeAnalyticsContainer = () => {
  const [searchTermIES, setSearchTermIES] = useState<string>("");
  const [selectedIES, setSelectedIES] = useState<string>("");
  const [isDropdownIESVisible, setIsDropdownIESVisible] = useState(false);

  const [selectedFaculty, setSelectedFaculty] = useState<string>("");
  const [isDropdownFacultiesVisible, setIsDropdownFacultiesVisible] = useState(false);
  const [searchTermFaculty, setSearchTermFaculty] = useState<string>("");
  const [faculties, setFaculties] = useState<OptionSelect[]>([]);

  const [selectedCareer, setSelectedCareer] = useState<string>("");
  const [isDropdownCareerVisible, setIsDropdownCareerVisible] = useState(false);
  const [searchTermCareer, setSearchTermCareer] = useState<string>("");
  const [careers, setCareers] = useState<OptionSelect[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [dataCareer, setDataCareer] = useState<OptionSelect>();

  const [comparationData, setComparationData] = useState<any>();

  const authContext = useContext(AuthContext);

  const user = authContext?.user;
  const { institute } = useAuth();

  const userAdminValue = user?.userData.role ?? "";

  const filteredIESOptions = dataJSON.institutes
    .map((institute: any) => {
      const instituteCode = Object.keys(institute)[0];
      const instituteData = institute[instituteCode][1];
      if (instituteData && instituteData.name) {
        const label = `${instituteData.name} || ${instituteData.headquarters[0].city}`;
        return { value: instituteCode, label };
      }
      return null;
    })
    .filter((option: OptionSelect) => option !== null)
    .filter((option: OptionSelect) =>
      option.label.toLowerCase().includes(searchTermIES.toLowerCase())
    );

  const filteredFacultyOptions = faculties
    .filter((option: OptionSelect) => option !== null)
    .filter((option: OptionSelect) =>
      option.label.toLowerCase().includes(searchTermFaculty.toLowerCase())
    );

  const filteredCareersOptions = careers
    . filter((option: OptionSelect) => option !== null)
    .filter((option: OptionSelect) =>
      option.label.toLowerCase().includes(searchTermCareer.toLowerCase())
    );

  const resetDependentFields = (level: "ies" | "faculty" | "career") => {
    switch (level) {
      case "ies":
        setSelectedFaculty("");
        setSearchTermFaculty("");
        setFaculties([]);
        setIsDropdownFacultiesVisible(false);
        // setComparationData(null)
        resetDependentFields("faculty"); // Recursivo para limpiar niveles inferiores
        break;
      case "faculty":
        setSelectedCareer("");
        setSearchTermCareer("");
        setCareers([]);
        setDataCareer(undefined);
        // setComparationData(null)
        setIsDropdownCareerVisible(false);
        break;
      case "career":
        setSelectedCareer("");
        setSearchTermCareer("");
        setDataCareer(undefined);
        // setComparationData(null)
        break;
      default:
        console.warn(`Unhandled reset level: ${level}`);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type: "ies" | "faculty" | "career"
  ) => {
    const value = e.target.value;
    // if(value.length <= 0 ) setComparationData(null)

    switch (type) {
      case "ies":
        setSearchTermIES(value);
        setIsDropdownIESVisible(!!value);
        if (!value){
          setSelectedIES(value);
          resetDependentFields("ies");
          setSelectedCareer('');
          setDataCareer(undefined);
        } 
        break;
      case "faculty":
        setSearchTermFaculty(value);
        setIsDropdownFacultiesVisible(!!value);
        if (!value){
          setSelectedFaculty(value);
          resetDependentFields("faculty");
          setSelectedCareer('');
          setDataCareer(undefined);
        } 
        break;
      case "career":
        setSearchTermCareer(value);
        setIsDropdownCareerVisible(!!value);
        if (!value){
          setSelectedCareer('');
          setDataCareer(undefined);
        }
        break;
      default:
        console.warn(`Unhandled input type: ${type}`);
    }
  };

  const handleSelectChange = (
    selectedValue: OptionSelect,
    type: "ies" | "faculty" | "career"
  ) => {
    switch (type) {
      case "ies":
        const prevIesValue = selectedIES;
        setSelectedIES(selectedValue.value);
        setSearchTermIES(selectedValue.label);
        setIsDropdownIESVisible(false);
        if(selectedValue.value != prevIesValue)
          resetDependentFields("ies");
        break;
      case "faculty":
        const prevFacultyValue = selectedFaculty;
        setSelectedFaculty(selectedValue.value);
        setSearchTermFaculty(selectedValue.label);
        setIsDropdownFacultiesVisible(false);
        if(selectedValue.value != prevFacultyValue)
          resetDependentFields("faculty");
        break;
      case "career":
        const careerVal = selectedValue.value || selectedValue.code || selectedValue.label;
        if(careerVal) {
          setDataCareer(selectedValue);
          setSelectedCareer(careerVal);
          setSearchTermCareer(selectedValue.label);
        }
        setIsDropdownCareerVisible(false);
        break;
      default:
        console.warn(`Unhandled select type: ${type}`);
    }
  };

  const getFaculties = useCallback(async () => {
    try {
      const currentInstId =
        userAdminValue === "Super Administrador"
          ? selectedIES
          : institute?.id || user?.userData.institute || "";

      const facultiesData =
        userAdminValue !== "Super Administrador"
          ? currentInstId && (await facultyService.getAll(currentInstId))
          : selectedIES && (await facultyService.getAll(selectedIES));

      if (facultiesData) {
        const formattedFaculties = facultiesData.map((faculty) => {
          const facultyRef =
            faculty.ref ||
            (currentInstId && faculty.id
              ? `institutes/${currentInstId}/faculties/${faculty.id}`
              : faculty.id || "");
          return {
            value: facultyRef,
            label: faculty.name,
          };
        });
        setFaculties(formattedFaculties);
        setIsDropdownFacultiesVisible(true);
      }
    } catch (error) {
      console.error("Error trying to get faculties", error);
    }
  }, [selectedIES, institute, userAdminValue, user?.userData.institute]);

  const getCareers = useCallback(async () => {
    try {
      const currentInstId =
        userAdminValue === "Super Administrador"
          ? selectedIES
          : institute?.id || user?.userData.institute || "";

      let getCareersData: any[] = [];
      if (userAdminValue === "Administrador Facultad") {
        const rawUid = user?.userData.uidFaculty;
        const uidFaculties = Array.isArray(rawUid) ? rawUid : (rawUid ? [rawUid] : []);
        const allCareersPromises = uidFaculties.map(async (facId) => {
          const ref = `${user?.userData.ref}/faculties/${facId}`;
          return await careersService.getAll(ref);
        });
        const results = await Promise.all(allCareersPromises);
        getCareersData = results.flat();
      } else {
        if (selectedFaculty) {
          getCareersData = await careersService.getAll(selectedFaculty);
        }
      }

      if (getCareersData && getCareersData.length > 0) {
        const formattedCareers = getCareersData.map((career) => {
          const careerCode = career.code || career.id || "";
          const careerRef =
            career.ref ||
            (selectedFaculty && careerCode
              ? `${selectedFaculty}/careers/${careerCode}`
              : careerCode || career.name);
          return {
            value: careerRef,
            label: `${career.name} || SNIES: ${careerCode}`,
            code: careerCode,
            institutionId: career.uidInstitution || currentInstId,
          };
        });
        setCareers(formattedCareers);
        setIsDropdownCareerVisible(true);
      }
    } catch (error) {
      console.error("Error trying to get careers", error);
    }
  }, [selectedFaculty, user, userAdminValue, selectedIES, institute?.id]);

  // const [selectedDays, setSelectedDays] = useState<string>();


  const getComparationData = useCallback(async (selectedDays: string) => {
    const code = dataCareer?.code;
    const institutionId = dataCareer?.institutionId;
    const days =  isNaN(Number(selectedDays)) ? 30 : Number(selectedDays);

    try {
      if (code && institutionId) {
        setIsLoading(true);
        await fetch(
          "https://us-central1-guia-universitaria-dev.cloudfunctions.net/getComparationTopByCareer",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              career_id: code,
              institute_id: institutionId,
              days: days,
            }),
          }
        )
          .then((res) => res.json())
          .then((data) => {
            setComparationData(data.flat());
            setIsLoading(false);
          });
      }
      setIsLoading(false)
    } catch (error) {
      console.error("Error trying to get getComparationData", error);
      setIsLoading(false)
    }
  }, [dataCareer?.code, dataCareer?.institutionId]);

  useEffect(() => {
    void getFaculties();
  }, [getFaculties]);

  useEffect(() => {
    void getCareers();
  }, [getCareers]);

  return (
    <ComparativeAnalyticsComponent
      userType={userAdminValue}
      handleInputChange={handleInputChange}
      handleSelectChange={handleSelectChange}
      selectedIES={selectedIES}
      searchTermIES={searchTermIES}
      optionsIES={filteredIESOptions}
      optionsFaculties={filteredFacultyOptions}
      searchTermFaculty={searchTermFaculty}
      isDropdownIESVisible={isDropdownIESVisible}
      isDropdownFacultiesVisible={isDropdownFacultiesVisible}
      selectedFaculty={selectedFaculty}
      optionsCareers={filteredCareersOptions}
      searchTermCareer={searchTermCareer}
      isDropdownCareerVisible={isDropdownCareerVisible}
      selectedCareer={selectedCareer}
      comparationData={comparationData}
      careerLabel={searchTermCareer}
      getComparationData={getComparationData}
      isLoading={isLoading}
      dataCareer={dataCareer}
    />
  );
};

export default ComparativeAnalyticsContainer;
